// src/components/RPA/ProjectMembersPanel.js RBAC patches 6.1.2026
// UPDATED: Owner-only gating for member management controls.
//
// KEY CHANGES:
//   - canManageRoles derived from project.myRole or project.accessLevel
//   - Add Member form hidden for non-owners (replaced with info banner)
//   - Remove button hidden for non-owners
//   - Role dropdowns disabled and read-only for non-owners
//   - All API calls still go through (backend also enforces ownership)

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import SidePanel from './ui/SidePanel';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const PROJECT_ROLES = ['viewer', 'contributor', 'manager', 'owner'];

const PROJECT_ROLE_DESCRIPTIONS = {
  viewer:      'Read-only access to project content',
  contributor: 'Can upload documents and run workflow steps',
  manager:     'Can manage members and project settings',
  owner:       'Full control including deletion',
};

const AUDIT_ROLES = [
  { value: '',                   label: '— Use project role default —' },
  { value: 'partner',            label: 'Engagement Partner' },
  { value: 'audit_manager',      label: 'Audit Manager' },
  { value: 'senior_auditor',     label: 'Senior Auditor' },
  { value: 'auditor',            label: 'Auditor' },
  { value: 'reviewer',           label: 'Reviewer' },
  { value: 'oversight_reviewer', label: 'Oversight Reviewer' },
];

const AUDIT_ROLE_DESCRIPTIONS = {
  partner:            'Signs final opinion, approves all workpapers',
  audit_manager:      'General reviewer, closes issues, assigns audit roles',
  senior_auditor:     'Detail reviewer, can elevate issue severity',
  auditor:            'Preparer, raises issues, adds comments',
  reviewer:           'Read access + responds to issues only',
  oversight_reviewer: 'Limited read-only access',
};

const PROJECT_ROLE_FALLBACK_LABEL = {
  owner:       'Audit Manager (default for owner)',
  manager:     'Senior Auditor (default for manager)',
  contributor: 'Auditor (default for contributor)',
  viewer:      'Reviewer (default for viewer)',
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const styles = {
  form: {
    marginBottom: 20,
    padding: 14,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    background: '#f9fafb',
  },
  formTitle: { fontWeight: 600, marginBottom: 12, fontSize: 14, color: '#111827' },
  fieldGroup: { marginBottom: 10 },
  label: { display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#374151' },
  hint: { fontSize: 10, color: '#6b7280', fontWeight: 400, marginLeft: 4 },
  input: {
    width: '100%', padding: '6px 10px', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit',
  },
  select: {
    width: '100%', padding: '6px 10px', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 13, background: '#fff', boxSizing: 'border-box', cursor: 'pointer',
  },
  addBtn: (disabled) => ({
    padding: '7px 16px',
    background: disabled ? '#9ca3af' : '#3b82f6',
    color: '#fff', border: 'none', borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontWeight: 500, fontSize: 13, marginTop: 4,
  }),
  errorBox: {
    background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
    padding: '10px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13,
  },
  infoBanner: {
    background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af',
    padding: '10px 14px', borderRadius: 8, marginBottom: 20, fontSize: 13,
  },
  sectionTitle: { fontWeight: 600, fontSize: 14, color: '#111827', marginBottom: 10 },
  memberCard: (isOwner) => ({
    border: '1px solid #e5e7eb',
    borderLeft: isOwner ? '3px solid #3b82f6' : '1px solid #e5e7eb',
    borderRadius: 8, padding: 12,
    background: isOwner ? '#f0f9ff' : '#fff',
    marginBottom: 10,
  }),
  memberHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10,
  },
  memberName: { fontWeight: 600, fontSize: 14, color: '#111827' },
  memberEmail: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 7px', borderRadius: 999,
    fontSize: 10, fontWeight: 600, marginLeft: 6, verticalAlign: 'middle',
    background:
      color === 'blue'   ? '#dbeafe' :
      color === 'purple' ? '#ede9fe' :
      color === 'green'  ? '#d1fae5' : '#f3f4f6',
    border: `1px solid ${
      color === 'blue'   ? '#3b82f6' :
      color === 'purple' ? '#8b5cf6' :
      color === 'green'  ? '#22c55e' : '#d1d5db'}`,
    color:
      color === 'blue'   ? '#1e40af' :
      color === 'purple' ? '#5b21b6' :
      color === 'green'  ? '#065f46' : '#374151',
  }),
  roleRow: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap',
  },
  roleLabel: { fontSize: 11, color: '#6b7280', width: 80, flexShrink: 0 },
  roleSelect: (disabled) => ({
    flex: 1, minWidth: 150, padding: '4px 8px', border: '1px solid #d1d5db',
    borderRadius: 4, fontSize: 12,
    background: disabled ? '#f3f4f6' : '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit',
  }),
  removeBtn: {
    padding: '4px 10px', background: '#fee2e2', border: '1px solid #fecaca',
    color: '#991b1b', borderRadius: 4, cursor: 'pointer', fontSize: 12,
    fontWeight: 500, flexShrink: 0,
  },
  effectiveLabel: (isExplicit) => ({
    fontSize: 10,
    color: isExplicit ? '#1e40af' : '#9ca3af',
    marginTop: 6, fontStyle: 'italic',
    paddingTop: 6, borderTop: '1px dashed #e5e7eb',
  }),
  noMembers: { padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 14 },
  loading: { padding: 20, textAlign: 'center', color: '#6b7280', fontSize: 13 },
  emptySearch: { padding: '6px 0', fontSize: 13, color: '#9ca3af' },
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function ProjectMembersPanel({ isOpen, onClose, project }) {
  const projectId = project?._id || project?.id;
  const titleText = project?.name ? `Members · ${project.name}` : 'Project Members';

  // Derive whether the current user can manage members.
  // getProjectsController now returns myRole ('owner' | 'manager' | etc.)
  // and accessLevel ('owner' | 'collaborator') for backwards compatibility.
  const canManageRoles =
    project?.myRole === 'owner' ||
    project?.accessLevel === 'owner';

  const [admins,       setAdmins]       = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');
  const [search,       setSearch]       = useState('');

  // Add-member form state
  const [selectedId,   setSelectedId]   = useState('');
  const [addRole,      setAddRole]      = useState('contributor');
  const [addAuditRole, setAddAuditRole] = useState('');

  // ── Single API call: all admins with membership status embedded ───────────
  const loadAdmins = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/admin/rpa/admins`, {
        params: { projectId },
        withCredentials: true,
      });
      setAdmins(res.data?.admins || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Reset and reload whenever panel opens or project changes
  useEffect(() => {
    if (!isOpen || !projectId) {
      setAdmins([]);
      setError('');
      setSelectedId('');
      setSearch('');
      return;
    }
    loadAdmins();
  }, [isOpen, projectId, loadAdmins]);

  // ── Derived lists from single response ────────────────────────────────────
  const members   = admins.filter(a => a.membership?.isMember === true);
  const available = admins.filter(a => !a.membership?.isMember);

  const filteredAvailable = search.trim()
    ? available.filter(a =>
        (a.name  || '').toLowerCase().includes(search.toLowerCase()) ||
        (a.email || '').toLowerCase().includes(search.toLowerCase())
      )
    : available;

  // ── Add member ────────────────────────────────────────────────────────────
  const onAddMember = async (e) => {
    e.preventDefault();
    if (saving || !selectedId || !addRole || !canManageRoles) return;

    setSaving(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/admin/rpa/projects/${projectId}/members`,
        { adminId: selectedId, role: addRole, auditRole: addAuditRole || null },
        { withCredentials: true }
      );
      await loadAdmins();
      setSelectedId('');
      setAddRole('contributor');
      setAddAuditRole('');
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to add member';
      setError(e?.response?.status === 403 ? `Permission denied: ${msg}` : msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Update project role ───────────────────────────────────────────────────
  const onUpdateRole = async (memberAdminId, newRole) => {
    if (!canManageRoles) return;
    try {
      await axios.patch(
        `${API_URL}/admin/rpa/projects/${projectId}/members/${memberAdminId}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      await loadAdmins();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to update role';
      alert(e?.response?.status === 403 ? `Permission denied: ${msg}` : msg);
    }
  };

  // ── Update audit role ─────────────────────────────────────────────────────
  const onUpdateAuditRole = async (memberAdminId, newAuditRole) => {
    if (!canManageRoles) return;
    try {
      await axios.patch(
        `${API_URL}/admin/rpa/projects/${projectId}/members/${memberAdminId}/role`,
        { auditRole: newAuditRole || null },
        { withCredentials: true }
      );
      await loadAdmins();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to update audit role';
      alert(e?.response?.status === 403 ? `Permission denied: ${msg}` : msg);
    }
  };

  // ── Remove member ─────────────────────────────────────────────────────────
  const onRemoveMember = async (memberAdminId) => {
    if (!canManageRoles) return;
    if (!window.confirm('Remove this member from the project?')) return;
    try {
      await axios.delete(
        `${API_URL}/admin/rpa/projects/${projectId}/members/${memberAdminId}`,
        { withCredentials: true }
      );
      await loadAdmins();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to remove member';
      if (e?.response?.status === 403)  alert(`Permission denied: ${msg}`);
      else if (msg.includes('owner'))   alert('Cannot remove the project owner.');
      else                              alert(msg);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <SidePanel isOpen={isOpen} title={titleText} onClose={onClose}>
      {!projectId ? (
        <div style={{ color: '#991b1b', padding: 12, fontSize: 13 }}>
          Missing project ID — re-open this panel from the project card.
        </div>
      ) : (
        <>
          {/* ── ADD MEMBER FORM (owners only) ──────────────────────────── */}
          {canManageRoles ? (
            <form onSubmit={onAddMember} style={styles.form}>
              <div style={styles.formTitle}>Add New Member</div>

              {/* Search filter */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Search staff</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="Filter by name or email…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              {/* Admin selection dropdown */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Staff member <span style={{ color: '#dc2626' }}>*</span>
                </label>
                {loading ? (
                  <div style={styles.emptySearch}>Loading…</div>
                ) : filteredAvailable.length === 0 ? (
                  <div style={styles.emptySearch}>
                    {search
                      ? 'No staff found matching that search'
                      : 'All verified staff are already members of this project'}
                  </div>
                ) : (
                  <select
                    style={styles.select}
                    value={selectedId}
                    onChange={e => setSelectedId(e.target.value)}
                    required
                  >
                    <option value="">— Select a staff member —</option>
                    {filteredAvailable.map(a => {
                      const key = a.adminId || a.id;
                      return (
                        <option key={key} value={key}>
                          {a.name ? `${a.name}  (${a.email})` : a.email}
                          {a.systemRole === 'system_admin' ? '  ★ System Admin' : ''}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>

              {/* Project role */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Project role <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <select
                  style={styles.select}
                  value={addRole}
                  onChange={e => setAddRole(e.target.value)}
                  required
                >
                  {PROJECT_ROLES.map(r => (
                    <option key={r} value={r}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                      {' '}— {PROJECT_ROLE_DESCRIPTIONS[r]}
                    </option>
                  ))}
                </select>
              </div>

              {/* Audit role */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Audit role
                  <span style={styles.hint}>(controls workpaper sign-off authority)</span>
                </label>
                <select
                  style={styles.select}
                  value={addAuditRole}
                  onChange={e => setAddAuditRole(e.target.value)}
                >
                  {AUDIT_ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 3, fontStyle: 'italic' }}>
                  {addAuditRole
                    ? AUDIT_ROLE_DESCRIPTIONS[addAuditRole]
                    : `Default: ${PROJECT_ROLE_FALLBACK_LABEL[addRole] || 'Reviewer'}`}
                </div>
              </div>

              <button
                type="submit"
                disabled={saving || !selectedId || loading}
                style={styles.addBtn(saving || !selectedId || loading)}
              >
                {saving ? 'Adding…' : 'Add Member'}
              </button>
            </form>
          ) : (
            <div style={styles.infoBanner}>
              🔒 Only project owners can add or remove members and update roles.
              Contact the project owner to request changes.
            </div>
          )}

          {/* ── ERROR ─────────────────────────────────────────────────── */}
          {error && <div style={styles.errorBox}>{error}</div>}

          {/* ── MEMBERS LIST ───────────────────────────────────────────── */}
          {loading ? (
            <div style={styles.loading}>Loading members…</div>
          ) : members.length === 0 ? (
            <div style={styles.noMembers}>
              No members yet. Add staff using the form above.
            </div>
          ) : (
            <>
              <div style={styles.sectionTitle}>
                Current Members ({members.length})
              </div>

              {members.map(member => {
                const m                  = member.membership || {};
                const isOwner            = m.role === 'owner';
                const hasCustomAuditRole = !!m.auditRole;
                const memberId           = member.adminId || member.id;

                // Dropdowns are disabled if: member is the owner OR current user cannot manage roles
                const dropdownDisabled = isOwner || !canManageRoles;

                return (
                  <div key={memberId} style={styles.memberCard(isOwner)}>
                    {/* Header */}
                    <div style={styles.memberHeader}>
                      <div>
                        <div style={styles.memberName}>
                          {member.name || member.email}
                          {isOwner && (
                            <span style={styles.badge('blue')}>OWNER</span>
                          )}
                          {member.systemRole === 'system_admin' && (
                            <span style={styles.badge('purple')}>SYS ADMIN</span>
                          )}
                        </div>
                        <div style={styles.memberEmail}>{member.email}</div>
                      </div>

                      {/* Remove button: only visible to owners, not for the owner member */}
                      {!isOwner && canManageRoles && (
                        <button
                          type="button"
                          onClick={() => onRemoveMember(memberId)}
                          style={styles.removeBtn}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    {/* Project role row */}
                    <div style={styles.roleRow}>
                      <span style={styles.roleLabel}>Project role</span>
                      <select
                        value={m.role || 'viewer'}
                        onChange={e => !dropdownDisabled && onUpdateRole(memberId, e.target.value)}
                        disabled={dropdownDisabled}
                        style={styles.roleSelect(dropdownDisabled)}
                      >
                        {PROJECT_ROLES.map(r => (
                          <option key={r} value={r}>
                            {r.charAt(0).toUpperCase() + r.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Audit role row */}
                    <div style={styles.roleRow}>
                      <span style={styles.roleLabel}>Audit role</span>
                      <select
                        value={m.auditRole || ''}
                        onChange={e => !dropdownDisabled && onUpdateAuditRole(memberId, e.target.value)}
                        disabled={dropdownDisabled}
                        style={styles.roleSelect(dropdownDisabled)}
                      >
                        {AUDIT_ROLES.map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Effective audit role */}
                    <div style={styles.effectiveLabel(hasCustomAuditRole)}>
                      <strong>Effective:</strong>{' '}
                      {m.effectiveAuditRole
                        ? m.effectiveAuditRole.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                        : '—'}
                      {!hasCustomAuditRole && m.role
                        ? ` (default from ${m.role} role)`
                        : ''}
                      {m.effectiveAuditRole && AUDIT_ROLE_DESCRIPTIONS[m.effectiveAuditRole]
                        ? ` · ${AUDIT_ROLE_DESCRIPTIONS[m.effectiveAuditRole]}`
                        : ''}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </>
      )}
    </SidePanel>
  );
}

export default ProjectMembersPanel;